import React from 'react';
import { ChildProps, DataValue, withApollo, WithApolloClient } from '@apollo/client/react/hoc';
import I18n from 'i18n-js';
import { differenceBy, flatten, get, isEmpty, isEqual, merge, uniq } from 'lodash';
import { withRouter, WithRouterProps } from 'react-router';
import { BumpDSACompliancePopover } from '@reverbdotcom/commons/src/components/bump_dsa_compliance_popover';
import {
  core_apimessages_Ad,
  core_apimessages_CollectionHeader_CollectionType,
  Core_Marketplace_CombinedMarketplaceSearchQuery,
  Core_Marketplace_CombinedMarketplaceSearchQueryVariables,
  reverb_search_ListingsSearchRequest_Sort,
  reverb_signals_Signal_Group,
} from '@reverbdotcom/commons/src/gql/graphql';
import { getPage, getTotalPages } from '../../pagination_helpers';
import CurationModal from '../discovery/curation/modal';
import ListingGrid, { ListingProps } from '../listing_grid';
import { listingsFilters, selectedFilters } from '../listings_filters';
import { MarketplacePathContext } from '../shared/url_helpers';
import HeaderWithMetadata from './header_with_metadata';
import {
  combinedMarketplaceSearchQuery,
  getCombinedMarketplaceInputs,
  includePromotedRows,
  mapParams,
  PageSettings,
  usingSignalSystemMarketplace,
} from './map_params';
import { applyAutodirect, autoselectedFilters, updateMarketplaceUrl } from './marketplace_autodirects';
import MarketplaceFallbackListings from './marketplace_fallback_listings';
import { pageViewTrackedExperiments } from './page_view_tracking_data';

import bind from '@reverbdotcom/commons/src/bind';
import { AdBanner } from '@reverbdotcom/commons/src/components/ads/ad_banner';
import { LIST_VIEW_STATE } from '@reverbdotcom/commons/src/components/list_view_toggle';
import RQLSearchGrid, { PaginationMode } from '@reverbdotcom/commons/src/components/rql_search_grid';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';
import { IUser, IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { BRAND_NEW } from '@reverbdotcom/commons/src/condition_helpers';
import { connect } from '@reverbdotcom/commons/src/connect';
import { EVERYWHERE_CODE, US_CON, currencies } from '@reverbdotcom/commons/src/constants';
import { trackCriteoProductList } from '@reverbdotcom/commons/src/criteo';
import * as elog from '@reverbdotcom/commons/src/elog';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { MParticlePageName, trackPageView } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { searchResultsSprigEvent } from '@reverbdotcom/commons/src/elog/sprig_events';
import experiments from '@reverbdotcom/commons/src/experiments';
import { getAllSelectedOptions, getFiltersForSidebar } from '@reverbdotcom/commons/src/grid_helpers';
import { isGreatValue } from '@reverbdotcom/commons/src/listing_great_value';
import { getItem, setItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { isBrandPage, isOutletHub, isShopPage, isUsingProximityFeatures } from '@reverbdotcom/commons/src/url_helpers';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { FavoriteButtonContainer } from '../favorites/favorite_button_container';
import CombinedBumpListingGrid from './combined_bump_listing_grid';
import { MarketplaceProviders } from './marketplace_providers';
import { withUrlContext, WithUrlProps } from '@reverbdotcom/commons/src/url_context';
import { HoistFavoriteCallout } from '../favorites/hoist_favorite_callout';
import { FavoriteButtonBanner } from '../favorites/favorite_button_banner';

interface RouterLocationProps {
  params: MarketplacePathContext;
}

interface AdWrapperProps {
  ad?: core_apimessages_Ad;
  adLoading?: boolean;
}

interface IExternalProps extends RouterLocationProps, AdWrapperProps {
  pageName: MParticlePageName;
  emptyGrid?: JSX.Element;
  showSold?: boolean;
  loading?: boolean;
  showFlags?: boolean;
  includeItemListMetadata?: boolean;
  saleType?: string;
}


interface PropsForQuery extends IUserContext, RouterLocationProps, WithUrlProps, AdWrapperProps {
  pageName?: MParticlePageName;
  showSold?: boolean;
  saleType?: string;
}

interface IState {
  listViewEnabled: boolean;
  exposedSidebarOpen: boolean;
}

type ICombinedProps = IUserContext & WithRouterProps & WithApolloClient<IExternalProps> & WithUrlProps;
export type MPProps = ChildProps<
  ICombinedProps, Core_Marketplace_CombinedMarketplaceSearchQuery, Core_Marketplace_CombinedMarketplaceSearchQueryVariables
>;
type MPQueryProps = React.PropsWithChildren<
  ChildProps<PropsForQuery, Core_Marketplace_CombinedMarketplaceSearchQuery, Core_Marketplace_CombinedMarketplaceSearchQueryVariables>
>;

interface PageConfigs { [k: string]: PageSettings }

const generatePageSettings = (params: MarketplacePathContext, showSold: boolean): PageConfigs => ({
  [MParticlePageName.Brand]: {
    pageName: MParticlePageName.Brand,
    renderCollectionHeaderDescription: true,
    collectionSlug: params.brand_slug,
    collectionType: core_apimessages_CollectionHeader_CollectionType.Brand,
  },
  [MParticlePageName.Shop]: {
    pageName: MParticlePageName.Shop,
    showSold,
    hideFollowButtons: true,
    pinTextQuery: true,
  },
  [MParticlePageName.Holiday]: {
    pageName: MParticlePageName.Holiday,
    hideFollowButtons: true,
    holidaySale: true,
    skipHeader: true,
  },
  [MParticlePageName.Sale]: {
    pageName: MParticlePageName.Sale,
    hideFollowButtons: true,
    collectionSlug: params.sale_slug,
    collectionType: core_apimessages_CollectionHeader_CollectionType.CuratedSet,
    skipHeader: true,
  },
  [MParticlePageName.Handpicked]: {
    pageName: MParticlePageName.Handpicked,
    renderCollectionHeaderDescription: true,
    collectionSlug: params.curated_set_slug,
    collectionType: core_apimessages_CollectionHeader_CollectionType.CuratedSet,
  },
});

function shouldFilterFeaturedWinnerRanks(queryParams: any, user: IUser, pageName: string): boolean {
  return !queryParams.sort &&
    !queryParams.show_only_sold &&
    !queryParams.condition &&
    user.shippingRegionCode === US_CON &&
    user.currency === currencies.USD &&
    pageName !== MParticlePageName.Shop;
}

export class SearchResultsPage extends React.Component<MPProps, IState> {
  state: IState = {
    listViewEnabled: getItem(LIST_VIEW_STATE),
    exposedSidebarOpen: false,
  };

  scrollTarget: ScrollTarget;

  @bind
  setListViewState(enabled: boolean) {
    setItem(LIST_VIEW_STATE, enabled);
    this.setState(
      { listViewEnabled: enabled },
      () => this.data.refetch({
        usingListView: enabled,
        shouldntLoadBumps: false,
        shouldntLoadSuggestions: false,
        useSignalSystem: usingSignalSystemMarketplace(),
        signalGroups: getSignalGroups(this.props.url, enabled),
      }),
    );
  }

  componentDidUpdate(prevProps: MPProps) {
    if (prevProps.url.query?.page !== this.props.url.query?.page) {
      this.scrollTarget.scrollToTop();
    }

    const isSearchLoaded = !this.data.loading;
    const isSearchUpdated = !isEqual(prevProps.data.listingsSearch, this.data.listingsSearch);

    const shouldTrackPageView = isSearchLoaded && isSearchUpdated;
    if (shouldTrackPageView) {
      this.trackPageView();

      const { user } = this.props;
      if (user && !user.loggedOut && isExperimentEnabled(user, experiments.WEB_CSAT)) {
        searchResultsSprigEvent(this.data.listingsSearch);
      }
    }

    applyAutodirect({ prevProps, props: this.props });
  }

  pageSettings(): PageSettings {
    const { params, showSold, pageName } = this.props;
    const pageConfigs = generatePageSettings(params, showSold || false);

    return pageConfigs[pageName];
  }

  /**
   * `this.data` returns a data object where listingsSearch, aggsSearch, and bumpedSearch results are always
   * available, even if stale. while loading new results, we display old results in the grid and filters with loading
   * ui via css, so this method ensures that previous results are present for that loading state.
   */
  get data(): DataValue<Core_Marketplace_CombinedMarketplaceSearchQuery, Core_Marketplace_CombinedMarketplaceSearchQueryVariables> {
    const safeData = this.props.data as any || {};
    const safePreviousData = safeData.previousData || {};

    return {
      ...safePreviousData,
      ...safeData,
    };
  }

  /**
   * `isAdOrSearchLoading()` was previously named `loading()`, and was renamed for clarity.
   *
   * Ad data and listings search data are always fetched in different queries,
   * and are not guaranteed to resolve their loading states during the same render cycle.
   *
   * `this.data` is resolved from the `Core_Marketplace_CombinedMarketplaceSearch` query and the `SearchResultsPage` component.
   * `this.props.adLoading` and `this.props.ad` are resolved from the `Core_Ad_Banner_Wrapper` query and the `MarketplacePageWrapper` component.
   */
  isAdOrSearchLoading() {
    return this.props.adLoading || isEmpty(this.data) || this.data.loading;
  }

  searchResult(): Core_Marketplace_CombinedMarketplaceSearchQuery['listingsSearch'] {
    return this.data?.listingsSearch;
  }

  bumpSearchResult(): Core_Marketplace_CombinedMarketplaceSearchQuery['listingsSearch'] {
    return this.data?.bumpedSearch;
  }

  filters() {
    const oldOrNewFilters = this.data?.aggsSearch?.filters;
    const filters = oldOrNewFilters || [];

    return listingsFilters(filters, this.props.url);
  }

  selectedFilters() {
    const oldOrNewFilters = this.data?.aggsSearch?.filters;
    const filters = oldOrNewFilters || [];
    return selectedFilters(filters);
  }

  getQuery(props: MPProps) {
    return props.data?.aggsSearch?.filters.find(f => f.key === 'TEXT_QUERY')?.options[0]?.optionValue;
  }

  getTotalText() {
    const count = this.searchResult()?.total || 0;

    return I18n.t(
      'discovery.resultsCount.countListings',
      {
        count,
        num: I18n.toNumber(count, { precision: 0 }),
      },
    );
  }

  getListings() {
    return this.searchResult()?.listings || [];
  }

  getBumpedListings() {
    return this.bumpSearchResult()?.listings || [];
  }

  defaultSort() {
    let defaultSort;
    const filters = this.filters();
    const sortFilter = filters.find(({ key }) => key === 'SORT_BY');
    if (sortFilter) {
      defaultSort = sortFilter.options.find(({ selected }) => !!selected)?.trackingValue;
    }
    return defaultSort || reverb_search_ListingsSearchRequest_Sort.NONE;
  }

  getFallbackListings() {
    const fallback = this.searchResult()?.fallbackListings || [];
    const listings = this.getListings();

    if (listings.length === 0) return fallback;

    return differenceBy(fallback, listings, 'id');
  }

  getSuggestedQuery() {
    const queries = this.searchResult()?.suggestedQueries || [];

    return queries?.[0];
  }

  shouldShowFallbackListings() {
    return this.getFallbackListings().length > 0;
  }

  renderFollowBanner() {
    if (this.pageSettings().hideFollowButtons) { return null; }
    if (this.isAdOrSearchLoading()) { return null; }
    if (!this.searchResult()) { return null; }
    if (this.shouldShowFallbackListings()) { return null; }
    const listings = this.getListings();
    const header = listings.length >= 5 ? I18n.t('discovery.favorites.banner.unsaved') : null;
    const subtext = listings.length >= 5 ? I18n.t('discovery.favorites.banner.notSaved.body.unfiltered') : null;

    return (
      <FavoriteButtonBanner
        header={header}
        subtext={subtext}
      />
    );
  }

  renderAdBanner() {
    const hasData = (!this.props.adLoading && !!this.props.ad?.imageAdCreative);
    if (!hasData) return null;
    // do not show ad banner if Shop Page
    if (this.pageSettings().pageName === MParticlePageName.Shop) return null;

    return (
      <div className="mt-6 mb-4 site-wrapper">
        <AdBanner
          ad={this.props.ad}
        />
      </div>
    );
  }

  shouldShowPromotedListings() {
    if (this.getListings().length === 0) return false;
    if (this.showingOnlySold()) return false;
    if (shouldFilterFeaturedWinnerRanks(this.props.url?.query, this.props.user, this.props.pageName)) return true;
    if (this.shouldShowFallbackListings()) return false;

    return includePromotedRows(this.props.params, this.props.url?.query);
  }

  didYouMeanSuggestion() {
    return this.searchResult()?.suggestions?.[0];
  }

  emptyGridExperimentOverride() {
    // overrides default empty grid if either experiment is active
    if (this.shouldShowFallbackListings()) return <div />;

    // uses default
    return this.props.emptyGrid;
  }

  showingOnlySold() {
    return !!this.props.url?.query?.show_only_sold;
  }

  @bind
  toggleExposedSidebarOpen() {
    this.setState((prevState) => ({ exposedSidebarOpen: !prevState.exposedSidebarOpen }));
  }

  hasSelectedFilterOptions() {
    const filtersForSidebar = getFiltersForSidebar(this.filters());

    return !!getAllSelectedOptions(filtersForSidebar).length;
  }

  @bind
  clearFiltersForMobileSidebar() {
    this.props.router.push(this.props.url.pathname);
  }

  renderCards() {
    const trackingSort = this.props.url?.query?.sort || this.defaultSort();
    const results = this.searchResult();
    const trackingPage = results != undefined ? getPage(results.offset, results.limit) : 0;
    const isShowingPromotedListings = this.shouldShowPromotedListings();
    const listingProps = {
      isListView: this.state.listViewEnabled,
      loading: this.isAdOrSearchLoading(),
      listings: this.getListings(),
      trackingQuery: this.trackingQuery(),
      trackingFilter: this.selectedFilters(),
      trackingSort: trackingSort,
      trackingPage: trackingPage,
      pageSettings: this.pageSettings(),
      filters: this.filters(),
      exposedSidebarOpen: this.state.exposedSidebarOpen,
      clearFiltersDisabled: this.hasSelectedFilterOptions(),
      toggleExposedSidebarOpen: this.toggleExposedSidebarOpen,
      listingSearchTotal: results?.total,
      clearFilters: this.clearFiltersForMobileSidebar,
      emptyGrid: this.emptyGridExperimentOverride(),
    } as ListingProps;

    if (isShowingPromotedListings) {
      return (
        <CombinedBumpListingGrid
          {...listingProps} // eslint-disable-line react/jsx-props-no-spreading
          bumpedListings={this.getBumpedListings()}
          error={this.data.error}
          includeItemListMetadata={this.props.includeItemListMetadata}
        />
      );
    }

    return (
      <ListingGrid
        {...listingProps} // eslint-disable-line react/jsx-props-no-spreading
        error={!!this.data.error}
        showFlags={this.props.showFlags}
        includeItemListMetadata={this.props.includeItemListMetadata}
      />
    );
  }

  renderFallbackCards() {
    return (
      <MarketplaceFallbackListings
        fallbackListings={this.getFallbackListings()}
        query={this.props.url.query.query}
        hasPrimaryListings={this.getListings().length > 0}
      />
    );
  }

  trackingQuery() {
    return this.props.url.query.query;
  }

  freeShippingListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => listing.shipping?.shippingPrices?.[0]?.rate?.amountCents === 0).map(l => l.id);
  }

  greatValueListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => isGreatValue(listing, this.props.user)).map(l => l.id);
  }

  returnPolicyListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => {
      if (listing.soldAsIs) { return; }
      const returnPolicyDays = listing.condition.conditionSlug === BRAND_NEW ? listing.shop?.returnPolicy?.newReturnWindowDays : listing.shop?.returnPolicy?.usedReturnWindowDays;
      return !!returnPolicyDays && returnPolicyDays >= 14;
    }).map(l => l.id);
  }

  get signals() {
    const listings = this.getListings();
    const signals = listings?.map(
      listing => listing?.signals?.map(signal => signal.name),
    );

    const formattedSignals = uniq(flatten(signals)).join('|');

    return formattedSignals;
  }

  sharedTrackingData() {
    const { listViewEnabled } = this.state;
    const { user, url, params } = this.props;
    const queryParams = url?.query || {};
    const results = this.searchResult();
    const trackedExperiments = pageViewTrackedExperiments({
      user,
    });

    const trackingData = {
      pageName: this.pageSettings().pageName || MParticlePageName.Marketplace,
      experiments: formatExperimentsForEventAttributes(this.props.user, trackedExperiments),
      query: this.trackingQuery(),
      autodirected: autoselectedFilters(this.props).length !== 0,
      sort: queryParams.sort || this.defaultSort(),
      filter: this.selectedFilters(),
      listingIds: results.listings?.map(l => l.id),
      querySuggestion: this.getSuggestedQuery(),
      page: getPage(results.offset, results.limit),
      totalPages: getTotalPages(results.total, results.limit),
      listingsCount: results.total,
      fallbackListingsCount: this.getFallbackListings().length,
      productType: queryParams.product_type || '',
      make: queryParams.make || '',
      category: queryParams.category || '',
      shopSlug: params.shop || '',
      brand: params.brand_slug || '',
      curatedSetSlug: params.curated_set_slug || '',
      postalCode: queryParams.postal_code,
      distance: queryParams.distance,
      view: listViewEnabled ? 'List' : 'Grid',
      freeShippingListingIds: this.freeShippingListingIds(),
      returnPolicyListingIds: this.returnPolicyListingIds(),
      refererSlug: queryParams.referer,
      signals: this.signals,
    };

    if (this.props.user.shippingRegionCode === 'GB' && this.greatValueListingIds().length > 0) {
      merge(trackingData, { greatValueListingIds: this.greatValueListingIds() });
    }

    if (this.shouldShowPromotedListings()) {
      merge(trackingData, { bumpedListingIds: this.getBumpedListings()?.map(l => l.id) });
    }

    return trackingData;
  }

  trackPageView() {
    trackPageView({
      ...this.sharedTrackingData(),
    });

    const results = this.searchResult();

    trackCriteoProductList({
      listingIds: results.listings.map(l => l.id),
      keywords: this.trackingQuery(),
      page_number: getPage(results.offset, results.limit),
    }, this.props.user);
  }

  renderGridLevelFollowButton() {
    if (this.pageSettings().hideFollowButtons) {
      return null;
    }

    return (
      <HoistFavoriteCallout anchor={
        <span className="d-inline-block">
          {this.renderGridLevelFollowButtonContainer()}
        </span>
      }
      />
    );
  }

  renderGridLevelFollowButtonContainer() {
    return (
      <FavoriteButtonContainer
        componentName="FavoriteButton (SearchOverview)"
        displayStyle="link"
        iconButtonSize="mini"
        iconButtonVariant="transparent"
        condensed
      />
    );
  }

  tipText() {
    if (isShopPage(this.props.url)) {
      return null;
    }

    return <BumpDSACompliancePopover />;
  }

  render() {
    const searchResult = this.searchResult();
    const hideClearFiltersButton = isEmpty(this.props.url?.query);
    if (this.data.error) {
      elog.error(
        'marketplace-render-failure',
        {
          networkErrorStack: this.data.error.networkError?.stack?.substring(0, 500),
        },
        this.data.error,
      );
    }

    return (
      <MarketplaceProviders
        updateUrl={(props) => updateMarketplaceUrl({ ...props })}
        keySuffix={String(this.state.listViewEnabled)}
      >
        {!this.pageSettings().skipHeader &&
          <HeaderWithMetadata
            params={this.props.params}
            query={get(this.props, 'location.query', {})}
            collectionType={this.pageSettings()?.collectionType}
            collectionSlug={this.pageSettings()?.collectionSlug}
            renderCollectionHeaderDescription={this.pageSettings().renderCollectionHeaderDescription}
            isBrandPage={isBrandPage(this.props.url)}
          />
        }
        <ScrollTarget onlyScrollUp ref={r => this.scrollTarget = r} />
        <RQLSearchGrid
          error={!!this.data.error}
          loading={this.isAdOrSearchLoading()}
          search={searchResult}
          filters={this.filters()}
          location={this.props.url}
          aboveGridBanner={this.renderAdBanner()}
          totalText={this.getTotalText()}
          followButton={this.renderGridLevelFollowButton()}
          cards={this.renderCards()}
          fallbackCards={this.renderFallbackCards()}
          paginationMode={PaginationMode.PAGINATED}
          hasSearchOverview
          followBanner={this.renderFollowBanner()}
          trackingQuery={this.trackingQuery()}
          setListViewState={this.setListViewState}
          showListViewToggle
          resultsTotal={searchResult?.total}
          query={this.props.url?.query?.query}
          pinTextQuery={!!this.pageSettings().pinTextQuery}
          toggleExposedSidebarOpen={this.toggleExposedSidebarOpen}
          showNewMobileSidebar
          hideClearFiltersButton={hideClearFiltersButton}
          clearFiltersUrl={this.props.url.pathname}
          tipText={this.tipText()}
        />
        <CurationModal />
      </MarketplaceProviders>
    );
  }
}

function getSignalGroups(location, usingListView) {
  const useProximitySignals = isUsingProximityFeatures(location);
  const signalGroups = [];
  if (usingListView) {
    signalGroups.push(useProximitySignals ? reverb_signals_Signal_Group.MP_LIST_PROX_CARD : reverb_signals_Signal_Group.MP_LIST_CARD);
  } else {
    signalGroups.push(useProximitySignals ? reverb_signals_Signal_Group.MP_GRID_PROX_CARD : reverb_signals_Signal_Group.MP_GRID_CARD);
  }

  return signalGroups;
}

export function mapVarsForMarketplaceQuery(
  props: MPQueryProps,
  bumpLimitOverride?: number,
  listingsLimitOverride?: number,
  queryOverride?: string,
  boostByBumpRateOverride?: boolean,
): Core_Marketplace_CombinedMarketplaceSearchQueryVariables {
  const { params, user, url, showSold, pageName, saleType } = props;

  const pageSettings = generatePageSettings(params, showSold)[pageName] || {};
  const pathParams = params;
  const queryParams = url?.query || {};
  const usingListView = getItem(LIST_VIEW_STATE) || false;

  const mapped = mapParams({
    pathParams,
    location: url,
    user,
    pageSettings: {
      holidaySale: pageSettings.holidaySale,
      showSold: pageSettings.showSold,
      pageName,
    },
    listingsLimitOverride,
    queryOverride,
    boostByBumpRateOverride,
    isOutlet: isOutletHub(url),
  });

  const shouldntLoadBumps = [
    pathParams.shop,
    pathParams.sale_slug && saleType == 'seller',
    queryParams.show_only_sold,
    queryParams.showsold,
    pageSettings.showSold,
    pageSettings.holidaySale,
    queryOverride,
  ].some(Boolean);

  return {
    // Variables for inputs
    ...getCombinedMarketplaceInputs({
      mapped,
      user,
      queryParams: url.query,
      bumpLimit: bumpLimitOverride,
      shouldFilterFeaturedWinnerRanks: shouldFilterFeaturedWinnerRanks(queryParams, user, pageName),
    }),
    // Variables for directives (https://graphql.org/learn/queries/#directives)
    shouldntLoadBumps,
    shouldntLoadSuggestions: false,
    usingListView,
    signalGroups: getSignalGroups(url, usingListView),
    useSignalSystem: usingSignalSystemMarketplace(),
  };
}

const withMarketplaceSearchQuery = withGraphql<
  ICombinedProps,
  Core_Marketplace_CombinedMarketplaceSearchQuery,
  Core_Marketplace_CombinedMarketplaceSearchQueryVariables
>(combinedMarketplaceSearchQuery, {
  options: (props) => {
    return {
      context: {
        headers: {
          ...(props.params.shop && { 'X-Item-Region': EVERYWHERE_CODE }),
        },
      },
      ssr: false,
      variables: mapVarsForMarketplaceQuery(props),
    };
  },
});

export default connect<IExternalProps>([
  withUserContext,
  withApollo,
  withRouter,
  withUrlContext,
  withMarketplaceSearchQuery,
])(SearchResultsPage);
